import { useState } from 'react'

import EmojiPicker from 'emoji-picker-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useSendStoryMessage } from 'api/stories/useSendMessage/useSendMessage'
import { Icons } from 'helpers/icons'
import colors from 'styles/colors'
import { type ReelMessage as ReelMessageType } from 'types/api/message'
import { type ReelActionType } from 'types/types'

interface ReelMessageProps {
  reelId: string
  setAction: (action: ReelActionType) => any
  setIsPaused: (x: boolean) => any
}

export const ReelMessage = ({
  reelId,
  setAction,
  setIsPaused
}: ReelMessageProps) => {
  const { t } = useTranslation()

  const { mutate: sendReelMessage, isLoading: sending } =
    useSendStoryMessage(reelId)

  const [toggleEmojiPicker, setToggleEmojiPicker] = useState(false)

  const [message, setMessage] = useState<ReelMessageType>({
    message: ''
  })

  const onEmojiClick = (emojiObject: { emoji: string }) => {
    const newText = `${message.message}${emojiObject.emoji}`

    setMessage((prevValue) => ({
      ...prevValue,
      message: newText
    }))
  }

  const clearTextArea = () => {
    setMessage((prevValue) => ({
      ...prevValue,
      message: ''
    }))
  }

  const handleSendMessage = () => {
    const trimmedMessage = message.message.trim()

    if (!trimmedMessage || sending) return

    setToggleEmojiPicker(false)
    setAction?.('longPress')

    sendReelMessage({
      ...message,
      message: trimmedMessage
    })

    clearTextArea()
    setIsPaused(false)
  }

  const handleTextClick = () => {
    document.getElementById('reelMessageInput')?.focus()
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage((prevValue) => ({
      ...prevValue,
      message: e.target.value
    }))
  }

  return (
    <ReelMessageContainer>
      <ReelMessageField>
        <ReelMessageTextArea
          id="reelMessageInput"
          onChange={handleTextChange}
          value={message.message}
          placeholder={`${t('message')}...`}
          onClick={handleTextClick}
        />

        <ReelMessageSmiley
          src={Icons.smile}
          alt={t('smile')}
          onClick={() => {
            setToggleEmojiPicker((prev) => !prev)
          }}
        />

        {toggleEmojiPicker && (
          <EmojiPickerContainer>
            <EmojiPicker onEmojiClick={onEmojiClick} width="26rem" />
          </EmojiPickerContainer>
        )}
      </ReelMessageField>

      <ReelMessageSendButton $sending={sending} onClick={handleSendMessage}>
        <ReelMessageSendButtonIcon src={Icons.lightSend} alt={t('send')} />
      </ReelMessageSendButton>
    </ReelMessageContainer>
  )
}

const ReelMessageContainer = styled.div`
  display: flex;
  align-items: flex-end;
  flex-grow: 1;
  margin-top: 1rem;
  margin-bottom: 1rem;
`

const ReelMessageField = styled.div`
  display: flex;
  align-items: center;
  width: 80%;
  position: relative;
  flex: 1 1;
`

const ReelMessageTextArea = styled.textarea`
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  letter-spacing: 0.02em;
  font-size: 1.4rem;
  line-height: 1.7rem;
  background: rgba(255, 255, 255, 0.2);
  border: 0.1rem solid rgba(38, 42, 51, 0.2);
  border-radius: 1.5rem;
  box-sizing: border-box;
  outline: none;
  width: 100%;
  resize: none;
  color: #f5f5f5;
  -ms-overflow-style: none;
  padding: 1.4rem 6.5rem 1.4rem 1.6rem;
  height: 5rem;

  &::placeholder,
  &::-webkit-input-placeholder,
  &:-moz-placeholder,
  &::-moz-placeholder,
  &:-ms-input-placeholder {
    color: #ffffff;
    opacity: 1;
  }
`

const ReelMessageSmiley = styled.img`
  position: absolute;
  right: 2rem;
  bottom: 1.4rem;
  filter: brightness(0) invert(1);
`

const ReelMessageSendButton = styled.div<{ $sending: boolean }>`
  background: ${colors.blue92deg};
  border: 0.3rem solid #76bcff;
  box-sizing: border-box;
  box-shadow: 0 0.4rem 1.4rem rgba(47, 152, 254, 0.5);
  border-radius: 16rem;
  width: 5rem;
  height: 5rem;
  position: relative;
  margin-left: 1rem;
  ${({ $sending }) => $sending && 'pointer-events: none; opacity: 0.7;'}
`

const ReelMessageSendButtonIcon = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(1);
`

const EmojiPickerContainer = styled.div`
  position: absolute;
  bottom: 110%;
  right: 0;
`